import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import API from '../api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  FiUserPlus, FiTrash2, FiEdit2, FiShield, FiUser,
  FiX, FiCheck, FiEye, FiEyeOff, FiMail, FiClock,
} from 'react-icons/fi';

const initForm = { username: '', password: '', fullName: '', email: '' };

// ── Reusable password input with eye toggle ──────────────────────────────────
function PasswordInput({ value, onChange, placeholder = 'Enter password...' }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        className="form-input"
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{ paddingRight: 42 }}
      />
      <button
        type="button"
        onClick={() => setShow(v => !v)}
        style={{
          position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text3)', padding: 0, display: 'flex', alignItems: 'center',
        }}
        tabIndex={-1}
      >
        {show ? <FiEyeOff size={15} /> : <FiEye size={15} />}
      </button>
    </div>
  );
}

// ── Avatar initials circle ───────────────────────────────────────────────────
function Avatar({ name, isMain }) {
  const initials = (name || '?')
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div style={{
      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
      background: isMain ? 'rgba(124,92,252,0.15)' : 'var(--bg3)',
      color: isMain ? 'var(--primary)' : 'var(--text3)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
    }}>
      {initials}
    </div>
  );
}

// ── Role badge ───────────────────────────────────────────────────────────────
function RoleBadge({ role }) {
  const isMain = role === 'main_admin';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
      background: isMain ? 'rgba(124,92,252,0.12)' : 'var(--bg3)',
      color: isMain ? 'var(--primary)' : 'var(--text2)',
      border: `1px solid ${isMain ? 'rgba(124,92,252,0.25)' : 'var(--border)'}`,
      whiteSpace: 'nowrap',
    }}>
      {isMain
        ? <><FiShield size={10} /> Main Admin</>
        : <><FiUser size={10} /> Admin</>}
    </span>
  );
}

// ── Admin row card (mobile-friendly alternative to table) ────────────────────
function AdminRow({ a, me, onEdit, onDelete }) {
  const isMe   = a.id === me.id;
  const isMain = a.role === 'main_admin';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '36px 1fr auto auto',
      alignItems: 'center',
      gap: 12,
      padding: '14px 16px',
      borderBottom: '1px solid var(--border)',
      transition: 'background 0.15s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {/* Avatar */}
      <Avatar name={a.full_name} isMain={isMain} />

      {/* Info */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
            {a.full_name || a.username}
          </span>
          {isMe && (
            <span style={{
              fontSize: 10, fontWeight: 700, color: 'var(--primary)',
              background: 'rgba(124,92,252,0.1)', padding: '1px 7px', borderRadius: 10,
            }}>
              you
            </span>
          )}
          <RoleBadge role={a.role} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>
            @{a.username}
          </span>
          {a.email && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text3)' }}>
              <FiMail size={10} /> {a.email}
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text3)' }}>
            <FiClock size={10} />
            {a.last_login
              ? new Date(a.last_login).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })
              : 'Never logged in'}
          </span>
        </div>
      </div>

      {/* Created date — hidden on small screens via opacity trick */}
      <span style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap', display: 'none' }}>
        {new Date(a.created_at).toLocaleDateString('en-IN')}
      </span>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button
          className="btn btn-outline btn-sm"
          style={{ padding: '5px 10px', borderRadius: 8 }}
          title="Edit admin"
          onClick={() => onEdit(a)}
        >
          <FiEdit2 size={13} />
        </button>
        {!isMain && !isMe && (
          <button
            className="btn btn-danger btn-sm"
            style={{ padding: '5px 10px', borderRadius: 8 }}
            title="Delete admin"
            onClick={() => onDelete(a.id, a.username)}
          >
            <FiTrash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function AdminManagement() {
  const { admin: me } = useAuth();
  const [admins,     setAdmins]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form,       setForm]       = useState(initForm);
  const [saving,     setSaving]     = useState(false);

  const fetchAdmins = async () => {
    try {
      const { data } = await API.get('/admins');
      setAdmins(data.admins);
    } catch {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const handleCreate = async () => {
    if (!form.username.trim() || !form.password.trim()) {
      toast.error('Username and password are required');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      await API.post('/admins', form);
      toast.success('Admin created');
      setForm(initForm);
      setShowForm(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create admin');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (editTarget.password && editTarget.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      await API.put(`/admins/${editTarget.id}`, {
        fullName: editTarget.fullName,
        email:    editTarget.email,
        ...(editTarget.password ? { password: editTarget.password } : {}),
      });
      toast.success('Admin updated');
      setEditTarget(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update admin');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, username) => {
    if (!window.confirm(`Delete admin "${username}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/admins/${id}`);
      toast.success('Admin deleted');
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete admin');
    }
  };

  if (me?.role !== 'main_admin') {
    return (
      <Layout>
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>
          <FiShield size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ fontSize: 14 }}>Only the main admin can manage users.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title">Admin Users</h1>
        <p className="page-subtitle">Manage admin accounts for SteponePay</p>
      </div>

      <div className="page-body fade-in">
        <div style={{ maxWidth: 760 }}>

          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <span style={{ fontSize: 13, color: 'var(--text3)' }}>
              {admins.length} admin{admins.length !== 1 ? 's' : ''}
            </span>
            <button
              className={`btn btn-sm ${showForm ? 'btn-outline' : 'btn-primary'}`}
              onClick={() => { setShowForm(v => !v); setForm(initForm); }}
            >
              {showForm
                ? <><FiX style={{ marginRight: 5 }} /> Cancel</>
                : <><FiUserPlus style={{ marginRight: 5 }} /> Add Admin</>}
            </button>
          </div>

          {/* ── Create form ── */}
          {showForm && (
            <div className="card fade-in" style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, color: 'var(--text)' }}>
                New Admin
              </h3>
              <div className="two-col">
                <div className="form-group">
                  <label className="form-label">Username *</label>
                  <input
                    className="form-input"
                    placeholder="john_doe"
                    value={form.username}
                    onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <PasswordInput
                    value={form.password}
                    placeholder="Min 6 characters"
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  />
                </div>
              </div>
              <div className="two-col">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    className="form-input"
                    placeholder="John Doe"
                    value={form.fullName}
                    onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    className="form-input"
                    type="email"
                    placeholder="john@stepone.com"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={saving}>
                  {saving
                    ? <span className="spinner" />
                    : <><FiCheck style={{ marginRight: 5 }} /> Create Admin</>}
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => { setShowForm(false); setForm(initForm); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ── Admins list ── */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>

            {/* List header */}
            <div style={{
              display: 'grid', gridTemplateColumns: '36px 1fr auto',
              gap: 12, padding: '10px 16px',
              borderBottom: '1px solid var(--border)',
              background: 'var(--bg3)',
            }}>
              <div />
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Admin
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Actions
              </span>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>
                <span className="spinner" style={{ borderTopColor: 'var(--primary)' }} />
              </div>
            ) : admins.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)', fontSize: 13 }}>
                No admins found
              </div>
            ) : (
              admins.map(a => (
                <AdminRow
                  key={a.id}
                  a={a}
                  me={me}
                  onEdit={a => setEditTarget({ id: a.id, fullName: a.full_name, email: a.email || '', password: '' })}
                  onDelete={handleDelete}
                />
              ))
            )}
          </div>

        </div>
      </div>

      {/* ── Edit modal ── */}
      {editTarget && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 20,
        }}
          onClick={e => { if (e.target === e.currentTarget) setEditTarget(null); }}
        >
          <div className="card fade-in" style={{ width: '100%', maxWidth: 440 }}>

            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Edit Admin</h3>
              <button
                className="btn btn-outline btn-sm"
                style={{ padding: '4px 8px' }}
                onClick={() => setEditTarget(null)}
              >
                <FiX size={14} />
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                className="form-input"
                value={editTarget.fullName}
                onChange={e => setEditTarget(t => ({ ...t, fullName: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className="form-input"
                type="email"
                value={editTarget.email}
                onChange={e => setEditTarget(t => ({ ...t, email: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                New Password{' '}
                <span style={{ color: 'var(--text3)', fontWeight: 400, fontSize: 11 }}>
                  (leave blank to keep current)
                </span>
              </label>
              <PasswordInput
                value={editTarget.password}
                placeholder="Enter new password..."
                onChange={e => setEditTarget(t => ({ ...t, password: e.target.value }))}
              />
              {editTarget.password && editTarget.password.length < 6 && (
                <p style={{ fontSize: 11, color: 'var(--error)', marginTop: 4 }}>
                  Must be at least 6 characters
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleUpdate}
                disabled={saving}
                style={{ flex: 1 }}
              >
                {saving ? <span className="spinner" /> : <><FiCheck style={{ marginRight: 5 }} /> Save Changes</>}
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => setEditTarget(null)}>
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}
    </Layout>
  );
}